import moment from "moment";
import { apis, RequestOptions } from "../../backend";
import { BasicFieldReference } from "../../models/BasicField";
import { UserCallStatus } from "../../models/CallStatisticsItem";
import LeadList, { LeadListContact } from "../../models/LeadList";
import { LeadListContactSummary } from "../../models/LeadListContactSummary";
import { LeadListContactSummaryElement } from "../../models/LeadListContactSummaryElement";
import PhoneNumber from "../../models/PhoneNumber";
import UserPreferences from "../../models/UserPreferences";
import { choose } from "../../utils";
import { CohearingMessage } from "../../models/CohearingMessage";
import { UserCohearingSession } from "../../models/UserCohearingSession";
import { CallCohearingSession } from "../../models/CallCohearingSession";
import { CallFilters } from "../../models/CallFilters";
import { isIncludedInProject } from "../../models/Project";
import { getTabId } from "../../utils/tabs";
import Call from "../../models/Call";
import { isPopupCaller } from "../../AppType";

const tabTimestamp = Date.now().toString();

export type ReservedContactMetadata = {
  leadList: LeadList;
  contactFields: BasicFieldReference[];
  phoneNumbers: string[];
};

/**
 * Returns whether the user has an active call or actively calling in another tab.
 * Use this function to disable calling in the current tab.
 */
export function isBusyInAnotherTab() {
  const otherTabTimestamp = localStorage.getItem('hasCallInTab');
  if (!otherTabTimestamp || new Date(Number(otherTabTimestamp)).getTime() < Date.now() - moment.duration(1, 'hour').asMilliseconds()) {
    return false;
  }

  return otherTabTimestamp !== tabTimestamp;
}

/**
 * Sets that a call is active or being dialed in this tab,
 * which disables calling in other tabs.
 */
export function setBusyInAnotherTab(flag: boolean) {
  if (isPopupCaller) {
    //  Gråar permanent ut ringaknappen i ursprungliga tabben.
    return;
  }

  const otherTabId = localStorage.getItem('hasCallInTab');

  if (flag) {
    localStorage.setItem('hasCallInTab', tabTimestamp);
  } else if (otherTabId === tabTimestamp) {
    localStorage.removeItem('hasCallInTab');
  }
}

export type ReserveContactResult = LeadListContact & ReservedContactMetadata & {
  dialGroup: LeadList | null;
};

/**
 * Reserves the next contact in the list, which locks the contact from being reserved by other users.
 * 
 * @param contactId Optional specific contact to reserve.
 */
export async function reserveContact(leadListId: string, contactId?: string): Promise<{ contact: LeadListContact, metadata: ReservedContactMetadata; }> {
  const contact = await apis.dial.put<ReserveContactResult>(`/dial-group-contacts-v1/${leadListId}/reserve`, {
    contactId
  });

  return {
    contact: new LeadListContact(contact),
    metadata: {
      leadList: contact.dialGroup ? new LeadList(contact.dialGroup) : null,
      contactFields: contact.contactFields,
      phoneNumbers: contact.phoneNumbers,
    }
  };
}

/**
 * Quarantines a contact.
 */
export async function quarantineContact(leadListId: string, contactId: string, releaseAt: Date) {
  await apis.dial.put(`/dial-group-contacts-v1/${leadListId}/quarantine/${contactId}`, { releaseAt });
}

export type TelephonyHeartbeat = {
  /**
   * Current status.
   */
  userStatus: UserCallStatus;

  /**
   * List of alternative IDs for the current call (EasyTelecom user call ID).
   */
  currentCallIds: string[] | undefined;

  /**
   * ID of a user which the current user is listening to.
   */
  currentCohearingUserId?: string;

  /**
   * If making predictive calls, then the ID of the current lead list.
   */
  currentPredictiveLeadListId: string | null;

  /**
   * If set, then currently vieweing a reserved contact, so do not unreserve it.
   */
  currentReservedContactId?: string;
}

export type TelephonyUserState = {
  predictiveModeDialGroupId?: string;
};

/**
 * Sends a heartbeat to the telephony service. This is required to keep various sessions alive.
 */
export async function createTelephonyHeartbeat(heartbeat: TelephonyHeartbeat): Promise<TelephonyUserState> {
  return await apis.dial.post<TelephonyUserState>('/heartbeat-v1', heartbeat, { tabId: getTabId() });
}

export async function getLeadListContactSummary(leadListId: string): Promise<LeadListContactSummary> {
  const summary = await apis.dial.get<LeadListContactSummary>(`/dial-group-contacts-v1/${leadListId}/summary`);
  return new LeadListContactSummary(summary);
}

export async function listLeadListContactSummaries(leadListIds: string[]): Promise<LeadListContactSummaryElement[]> {
  const summaries = await apis.dial.get<LeadListContactSummaryElement[]>
    (`/dial-group-contacts-v1/summaries`, { dialGroupIds: leadListIds }, { overflowQueryToHeader: true });
  return summaries.map(summary => new LeadListContactSummaryElement(summary));
}

export type LeadListContactQuery = {
  contactIds?: string[];
  dialGroupIds?: string[];
}

export async function listLeadListContacts(query: LeadListContactQuery, options?: RequestOptions): Promise<LeadListContact[]> {
  const contacts = await apis.dial.get<LeadListContact[]>(`/dial-group-contacts-v1`, query, {
    overflowQueryToHeader: true,
    ...options ?? {},
  });

  return contacts.map(contact => new LeadListContact(contact));
}

export async function updateLeadListContact(leadListId: string, contactId: string, update: Partial<LeadListContact>) {
  await apis.dial.put(`/dial-group-contacts-v1/${leadListId}/contacts/${contactId}`, update);
}

export type ChooseCallerPhoneNumberParams = {
  preferences: UserPreferences;
  projectId?: string;
};

/**
 * Chooses a phone number to call from.
 * If `user` has a preferred outbound (caller) phone number and it exists in the list, then
 * that phone number is chosen. Otherwise, a random number is chosen.
 */
export function chooseCallerPhoneNumber(phoneNumbers: PhoneNumber[], { preferences, projectId }: ChooseCallerPhoneNumberParams) {
  phoneNumbers = phoneNumbers.filter(phoneNumber => phoneNumber.isUsable() && phoneNumber.isOutboundEnabled);
  const allPhoneNumbers = phoneNumbers;

  console.debug("[chooseCallerPhoneNumber] Available numbers: %s",
    phoneNumbers.map(phoneNumber => phoneNumber.phoneNumber).join(', '));

  if (projectId) {
    phoneNumbers = allPhoneNumbers.filter(phoneNumber => isIncludedInProject(phoneNumber, projectId));

    console.debug("[chooseCallerPhoneNumber] Available numbers in project %s: %s",
      projectId,
      phoneNumbers.map(phoneNumber => phoneNumber.phoneNumber).join(', '));
  }

  const preferredPhoneNumbers = preferences.listCallerPhoneNumbers(phoneNumbers);

  if (preferredPhoneNumbers.length) {
    phoneNumbers = phoneNumbers.filter(phoneNumber => preferredPhoneNumbers.includes(phoneNumber));

    console.debug("[chooseCallerPhoneNumber] Available numbers that are preferred (%s): %s",
      preferredPhoneNumbers.map(phoneNumber => phoneNumber.phoneNumber).join(', '),
      phoneNumbers.map(phoneNumber => phoneNumber.phoneNumber).join(', '));
  }

  if (preferredPhoneNumbers.length && !phoneNumbers.length) {
    phoneNumbers = preferredPhoneNumbers;

    console.debug("[chooseCallerPhoneNumber] Falling back to preferred phone numbers");
  }

  if (!phoneNumbers.length) {
    phoneNumbers = allPhoneNumbers;

    console.debug("[chooseCallerPhoneNumber] Falling back to any phone number");
  }

  return choose(phoneNumbers);
}

export type CallSelector = { callId?: string; easyTelecomUserCallId?: string; recipient?: string; };

export type CallContactInformation = { contactId: string; contactInfo?: BasicFieldReference[]; };

/**
 * Fetches contact information for an ongoing call.
 */
export async function getCallContactInformation(selector: CallSelector) {
  return await apis.dial.get<CallContactInformation>('/call-contact-v1', selector);
}

/**
 * Lists users which are connected to the dial service and able to receive inbound calls.
 */
export async function listConnectedUsers() {
  return await apis.dial.get<{ userId: string; }[]>('/call-transfers-v1/users');
}

/**
 * Transfers an active call to another user.
 */
export async function transferCallToUser(callId: string, userId: string) {
  return await apis.dial.post(`/call-transfers-v1/${callId}/users/${userId}`);
}

/**
 * Reserves an incoming call.
 * This will briefly prevent another user from being notified of the call, for during initialization of the dial service.
 */
export async function reserveInboundCall(callId: string) {
  await apis.dial.put(`/inbound-calls-v1/reserve/${callId}`);
}

/**
 * Declines an incoming call.
 */
export async function declineInboundCall(callId: string) {
  await apis.dial.del(`/inbound-calls-v1/decline/${callId}`);
}

export async function createExternalCall(call: Call) {
  return await apis.dial.post<{ callId: string; contactId?: string; }>('/easytelecom-v1/external-call', call);
}

type ListCohearingMessagesOptions = {
  createdAfter?: Date;
  acknowledged?: boolean;
}

export async function listCohearingMessages(options: ListCohearingMessagesOptions): Promise<CohearingMessage[]> {
  const messages = await apis.dial.get<CohearingMessage[]>(`/cohearing-v1/messages`, options);
  return messages.map(message => new CohearingMessage(message));
}

export async function acknowledgeCohearingMessage(messageId: string) {
  await apis.dial.post(`/cohearing-v1/messages/${messageId}/acknowledge`);
}

type ListCohearingSessionsOptions = {
  userId?: string;
  listenerUserId?: string;
  isActive?: boolean;
}

export async function listUserCohearingSessions(options: ListCohearingSessionsOptions): Promise<UserCohearingSession[]> {
  const sessions = await apis.dial.get<UserCohearingSession[]>(`/cohearing-v1/user`, options);
  return sessions.map(session => new UserCohearingSession(session));
}

type ListCallCohearingSessionsElement = {
  callId: string;
  cohearingSessions: CallCohearingSession[];
};

export async function listCallCohearingSessions(query: CallFilters): Promise<ListCallCohearingSessionsElement[]> {
  const elements = await apis.dial.get<ListCallCohearingSessionsElement[]>('/call-information-v1/cohearing-sessions', query);
  return elements.map(element => ({
    ...element,
    cohearingSessions: element.cohearingSessions.map(session => new CallCohearingSession(session))
  }));
}

export async function takeOverCall(easyTelecomUserCallIds?: string[]) {
  await apis.dial.post('/cohearing-v1/take-over', { easyTelecomUserCallIds });
}